<template>
  <div>
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" />
    </div>

    <div v-else-if="error" class="text-center py-5">
      <h4 class="text-danger">{{ error }}</h4>
      <b-button variant="outline-primary" @click="$router.push('/setup/digital-agreements')">
        Back to List
      </b-button>
    </div>

    <div v-else-if="agreement">
      <b-card>
        <div class="d-flex justify-content-between align-items-start flex-wrap mb-2">
          <div>
            <h4 class="mb-1">{{ agreement.subject || 'Digital Agreement' }}</h4>
            <b-badge :variant="statusVariant(agreement.status)" class="mb-1">
              {{ formatStatus(agreement.status) }}
            </b-badge>
          </div>
          <div class="d-flex flex-wrap gap-2">
            <b-button
              v-if="agreement.status === 'signed'"
              variant="outline-success"
              size="sm"
              :disabled="downloading"
              @click="handleDownload"
            >
              <b-spinner v-if="downloading" small class="mr-50" />
              <feather-icon v-else icon="DownloadIcon" size="14" class="mr-50" />
              Download Signed
            </b-button>
            <b-button
              v-if="agreement.status === 'pending' || agreement.status === 'sent'"
              variant="outline-warning"
              size="sm"
              :disabled="resending"
              @click="handleResend"
            >
              <b-spinner v-if="resending" small class="mr-50" />
              Resend
            </b-button>
            <b-button variant="outline-secondary" size="sm" @click="$router.push('/setup/digital-agreements')">
              Back
            </b-button>
          </div>
        </div>

        <b-row>
          <b-col md="6">
            <p><strong>Recipient:</strong> {{ agreement.recipient_name }}</p>
          </b-col>
          <b-col md="6">
            <p><strong>Email:</strong> {{ agreement.recipient_email }}</p>
          </b-col>
          <b-col md="6" v-if="agreement.sent_at">
            <p><strong>Sent At:</strong> {{ formatDate(agreement.sent_at) }}</p>
          </b-col>
          <b-col md="6" v-if="agreement.signed_at">
            <p><strong>Signed At:</strong> {{ formatDate(agreement.signed_at) }}</p>
          </b-col>
          <b-col md="6" v-if="agreement.rejected_at">
            <p><strong>Rejected At:</strong> {{ formatDate(agreement.rejected_at) }}</p>
          </b-col>
          <b-col md="12" v-if="agreement.rejection_reason">
            <p><strong>Rejection Reason:</strong> {{ agreement.rejection_reason }}</p>
          </b-col>
        </b-row>
      </b-card>

      <b-card class="mt-2" title="Agreement Content">
        <div class="agreement-html-content" v-html="agreement.content || agreement.html_content" />
      </b-card>

      <b-card v-if="agreement.signature" class="mt-2" title="Signature">
        <img :src="agreement.signature" alt="Signature" class="signature-preview" />
      </b-card>

      <b-card class="mt-2" title="Audit Trail">
        <div v-if="auditTrail.length === 0" class="text-muted">No audit events recorded.</div>
        <app-timeline v-else>
          <app-timeline-item
            v-for="(event, index) in auditTrail"
            :key="index"
            :variant="auditVariant(event.action)"
            :icon="auditIcon(event.action)"
          >
            <div class="d-flex flex-sm-row flex-column flex-wrap justify-content-between mb-1">
              <h6 class="mb-0 text-capitalize">{{ event.action || event.event }}</h6>
              <small class="text-muted">{{ formatDate(event.timestamp || event.created_at) }}</small>
            </div>
            <p v-if="event.user || event.performed_by" class="mb-25">
              By: {{ event.user || event.performed_by }}
            </p>
            <p v-if="event.details || event.description" class="mb-0 text-muted">
              {{ event.details || event.description }}
            </p>
          </app-timeline-item>
        </app-timeline>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BBadge,
  BSpinner,
} from 'bootstrap-vue'
import AppTimeline from '@core/components/app-timeline/AppTimeline.vue'
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue'
import moment from 'moment'
import {
  getDigitalAgreement,
  resendDigitalAgreement,
  downloadSignedAgreement,
} from '@/api/digitalAgreement'

export default {
  name: 'DigitalAgreementDetail',
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BBadge,
    BSpinner,
    AppTimeline,
    AppTimelineItem,
  },
  data() {
    return {
      loading: true,
      downloading: false,
      resending: false,
      error: null,
      agreement: null,
    }
  },
  computed: {
    auditTrail() {
      return this.agreement?.audit_trail || this.agreement?.audit_logs || []
    },
  },
  async mounted() {
    await this.loadAgreement()
  },
  methods: {
    async loadAgreement() {
      this.loading = true
      this.error = null
      try {
        const response = await getDigitalAgreement(this.$route.params.id)
        this.agreement = response.data?.data || response.data
      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to load agreement details.'
      } finally {
        this.loading = false
      }
    },
    formatStatus(status) {
      if (!status) return 'Pending'
      return status.charAt(0).toUpperCase() + status.slice(1)
    },
    statusVariant(status) {
      const map = {
        pending: 'warning',
        sent: 'info',
        signed: 'success',
        rejected: 'danger',
        expired: 'secondary',
      }
      return map[status] || 'secondary'
    },
    formatDate(value) {
      if (!value) return '-'
      return moment(value).format('DD/MM/YYYY HH:mm')
    },
    auditVariant(action) {
      const map = {
        sent: 'info',
        signed: 'success',
        rejected: 'danger',
        resent: 'warning',
        viewed: 'primary',
      }
      return map[action] || 'secondary'
    },
    auditIcon(action) {
      const map = {
        sent: 'SendIcon',
        signed: 'CheckIcon',
        rejected: 'XIcon',
        resent: 'RefreshCwIcon',
        viewed: 'EyeIcon',
      }
      return map[action] || 'CircleIcon'
    },
    async handleResend() {
      this.resending = true
      try {
        await resendDigitalAgreement(this.$route.params.id)
        this.$bvToast.toast('Agreement resent successfully.', { title: 'Success', variant: 'success' })
        await this.loadAgreement()
      } catch (err) {
        this.$bvToast.toast(err.response?.data?.message || 'Failed to resend.', {
          title: 'Error',
          variant: 'danger',
        })
      } finally {
        this.resending = false
      }
    },
    async handleDownload() {
      this.downloading = true
      try {
        const response = await downloadSignedAgreement(this.$route.params.id)
        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `signed-agreement-${this.$route.params.id}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (err) {
        this.$bvToast.toast(err.response?.data?.message || 'Failed to download.', {
          title: 'Error',
          variant: 'danger',
        })
      } finally {
        this.downloading = false
      }
    },
  },
}
</script>

<style scoped>
.agreement-html-content {
  font-size: 0.95rem;
  line-height: 1.6;
  max-height: 400px;
  overflow-y: auto;
}

.signature-preview {
  max-width: 320px;
  max-height: 160px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fafafa;
}

.gap-2 {
  gap: 0.5rem;
}
</style>
